import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { RelationIcon } from '@/shared/ui';

import { groupRelations } from '../model/types';
import type { GraphNeighbour, RelationDirection } from '../model/types';
import styles from './ElementScreen.module.css';

// Decyzja 58 (*typ relacji niosą wyłącznie kształty, kolor zostaje wolny dla stanów*) —
// kształty mieszkają w `shared/ui/RelationIcon` jako własne SVG (spec 12B.7), wspólne
// z drzewkiem. Stała szerokość znacznika zostaje w CSS, bo bez niej nazwy rozjeżdżają
// się w pionie między sekcjami.

/**
 * Decyzja 51 (*wszystkie powiązania na jednym wcięciu, stała kolejność
 * `has` → `depends-on` → `used-by`*). `part-of` idzie na koniec, bo przy
 * drzewie z jednym rodzicem (decyzja 44) jest wartością logiczną, nie listą —
 * pozycja co najwyżej jedna.
 */
const ORDER: readonly RelationDirection[] = ['has', 'depends-on', 'used-by', 'part-of'];

interface RelationSectionsProps {
  relations: readonly GraphNeighbour[];
}

export function RelationSections({ relations }: RelationSectionsProps) {
  const { t } = useTranslation();
  const groups = groupRelations(relations);

  const present = ORDER.filter((direction) => groups[direction].length > 0);

  if (present.length === 0) {
    return <p className={styles['empty']}>{t('graph.element.noRelations')}</p>;
  }

  return (
    <>
      {present.map((direction) => (
        <div key={direction} className={styles['relationGroup']}>
          <h3 className={styles['relationGroupTitle']}>
            <span className={styles['relationMark']} aria-hidden="true">
              <RelationIcon direction={direction} size={14} />
            </span>
            {t(`graph.relation.${direction}`)}
            <span className={styles['badge']}>{groups[direction].length}</span>
          </h3>

          <ul className={styles['relationList']}>
            {groups[direction].map((relation) => (
              <li key={relation.relationId} className={styles['relationRow']}>
                {/* Znacznik jest ozdobą wiersza — sens niesie nagłówek sekcji,
                    więc czytnik ekranu nie ma go powtarzać przy każdej pozycji. */}
                <span className={styles['relationMark']} aria-hidden="true">
                  <RelationIcon direction={direction} size={13} />
                </span>

                {/* Prawdziwy link, nie `onClick` na `div` — musi działać
                    środkowy przycisk myszy, „otwórz w nowej karcie" i kopiowanie
                    adresu. Cała platforma jest sterowana URL-em (spec 4A). */}
                <Link className={styles['relationLink']} to={`/element/${relation.elementId}`}>
                  {relation.title}
                </Link>

                {relation.type ? (
                  <span className={styles['relationType']}>{relation.type}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}
